import { useCodeGenerationState } from '@common/git/CodeGeneration/context';
import { usePrCreationTemplate } from '@common/git/git-hooks';
import { useGitInfo } from '@common/git/useGitInfo';
import { QueryKeys } from '@common/queries/common';
import { GitQueryKeys } from '@common/queries/GitQueryKeys';
import {
  fetchProjectSettings,
  ProjectSettings,
  updateArtefactIDMutation,
  updateParentArtefactIDForProjectMutation,
  updateProject
} from '@common/queries/project';
import { Entity } from '@common/types/Entity';
import { ReleaseRevampAnalytics } from '@common/user-analytics/deployment';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { BackEndCodeLanguage, BuildSystem, SyncMode, SyncModeType } from '@prophecy/graphql-types/enums';
import { InfoCircleIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import {
  Button,
  Centered,
  Controller,
  Editor,
  FooterHeight,
  Input,
  RadioButtonGroup,
  Select,
  Spinner,
  Stack,
  StackItem,
  Switch,
  Tabs,
  Text,
  Tooltip
} from '@prophecy/ui-v3';
import { StyledHelpText } from '@prophecy/ui-v3/Input/styled';
import { StyledRadioItem } from '@prophecy/ui-v3/Radio/styled';
import { Alert, toast } from '@prophecy/ui-v3/ui-compat';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { validateEntityName } from '@prophecy/utils/validation';
import { isValidYML } from '@prophecy/utils/validation-yaml';
import { useQueryClient } from '@tanstack/react-query';
import { DIRTY_SET_VALUE_CONFIG } from 'frontend/core/src/constants/form';
import { useGraphQlMutation, useGraphQlQueryFn } from 'frontend/core/src/data/util';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../../../context/mixpanel/context';
import { BuildOptions } from '../../config/project';
import {
  ArtefactIdLabel,
  ArtefactRequireMessage,
  ParentArtefactIdLabel,
  ParentArtefactRequiredMessage
} from './Components/ArtefactForm';
import { getTemplate, SettingTabKey, useBuildSystem, useBuildSystemSwitch } from './Components/BuildSystemOptions';
import { CheckForTemplateUpgrade } from './Components/CheckForTemplateUpgrade';
import { InlineSwitchTabs } from './styled';

const ArtefactNoteLine1 = `Prophecy projects can be released and used by other prophecy projects. For this we need to be able to have
              unique build artifact names for each prophecy project.`;
const ArtefactNoteLine2 = `For example, let’s say we have a project with Team1 as parentArtifactID and HelloWorld as artifactID. When
              we release a new version, say V2 of that project:`;
const ArtefactNoteLine3 = `- The unique maven coordinates would be Team1:HelloWorld:V2 (For Scala Projects)`;
const ArtefactNoteLine4 = `- The unique wheel package name would be Team1-HelloWorld==V2 (For Python Projects)`;

const SaveButton = styled(Button)`
  position: fixed;
  bottom: calc(${FooterHeight} + ${theme.spaces.x20});
  right: ${theme.spaces.x20};
` as unknown as typeof Button;
const StyledText = styled.span`
  display: inline-block;
  margin-top: ${theme.spaces.x6};
`;

const StyledRadioGroup = styled(RadioButtonGroup)`
  ${StyledRadioItem} {
    align-items: flex-start;
  }
`;

type FormData = {
  changelogFile: string;
  buildSystemTemplatesEnabled: boolean;
  templateId?: string;
  template: string;
  prTemplate: string;
  prTemplateEnabled: boolean;
  artefactID: string;
  parentArtefactID: string;
};

type SettingsProps = {
  uid: string;
  language: string;
  templateId?: string;
  isReadMode: boolean;
  syncMode: SyncModeType;
  branch: string;
  data: ProjectSettings<false>;
  isProjectAdmin?: boolean;
};

type AdvancedProps = Omit<SettingsProps, 'data'> & {
  queriedBranch?: string;
  isSqlProject?: boolean;
  isSimpleVersionControl?: boolean;
};

function GeneralSettingsTab({ uid, data, syncMode, isReadMode, language }: SettingsProps) {
  const queryClient = useQueryClient();
  const showPRTemplate = syncMode === SyncMode.External;
  const {
    actions: { handleRebuildCode },
    isLoading
  } = useCodeGenerationState();
  const isSqlProject = BackEndCodeLanguage.sql === language;
  const isArtefactFormEnabled = [BackEndCodeLanguage.scala, BackEndCodeLanguage.python].includes(
    language as BackEndCodeLanguage
  );
  const artefactIDMutation = useGraphQlMutation(updateArtefactIDMutation);
  const parentArtefactIDForProjectMutation = useGraphQlMutation(updateParentArtefactIDForProjectMutation);
  const {
    isInitialLoading: isPullRequestDataLoading,
    pullRequestData,
    prChangeInProgress,
    handlePRTemplateChange
  } = usePrCreationTemplate({ uid, enabled: showPRTemplate });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      changelogFile: '',
      buildSystemTemplatesEnabled: false,
      template: '',
      templateId: '',
      artefactID: '',
      parentArtefactID: '',
      prTemplate: '',
      prTemplateEnabled: false
    }
  });
  const changelogFile = watch('changelogFile');
  const templateId = watch('templateId');
  const template = watch('template');
  const buildSystemTemplatesEnabled = watch('buildSystemTemplatesEnabled');
  const artefactID = watch('artefactID');
  const parentArtefactID = watch('parentArtefactID');
  const prTemplate = watch('prTemplate');
  const prTemplateEnabled = watch('prTemplateEnabled');
  const {
    handleTemplateSwitch,
    handleChangeLogFile,
    isLoading: isBuildSystemMutationLoading
  } = useBuildSystemSwitch({ uid });

  useEffect(() => {
    if (pullRequestData) {
      const prTemplate = pullRequestData.prTemplate as string;
      const prTemplateEnabled = pullRequestData.isPrCreationEnabled;
      setValue('prTemplate', prTemplate, DIRTY_SET_VALUE_CONFIG);
      setValue('prTemplateEnabled', prTemplateEnabled, DIRTY_SET_VALUE_CONFIG);
      trigger();
    }
  }, [pullRequestData, setValue, trigger]);
  useEffect(() => {
    if (data) {
      const { buildSystemTemplatesEnabled, defaultTemplate, templates, changelogFile, artefactID, parentArtefactID } =
        data;

      const { templateId, templateValue } = getTemplate(defaultTemplate, templates);

      setValue('buildSystemTemplatesEnabled', Boolean(buildSystemTemplatesEnabled), DIRTY_SET_VALUE_CONFIG);
      setValue('template', templateValue, DIRTY_SET_VALUE_CONFIG);
      setValue('templateId', templateId, DIRTY_SET_VALUE_CONFIG);
      setValue('changelogFile', changelogFile, DIRTY_SET_VALUE_CONFIG);

      setValue('artefactID', artefactID, DIRTY_SET_VALUE_CONFIG);
      setValue('parentArtefactID', parentArtefactID, DIRTY_SET_VALUE_CONFIG);
      trigger();
    }
  }, [data, setValue, trigger]);

  const {
    handleSubmit: handleSubmitTemplate,
    showDiff,
    toggleDiff,
    restoreDefaultTemplateMutation,
    isUpdating
  } = useBuildSystem({
    uid,
    language,
    template: template,
    templateId: templateId
  });

  if (isPullRequestDataLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }

  const isPython = language === BackEndCodeLanguage.python;
  const entityName = isPython ? BuildSystem.wheel : BuildSystem.maven;

  async function submit(formData: FormData) {
    const {
      buildSystemTemplatesEnabled,
      defaultTemplate = '',
      templates = [],
      changelogFile,
      artefactID,
      parentArtefactID
    } = data || {};
    const { prTemplate, isPrCreationEnabled } = pullRequestData || {};
    const { templateValue } = getTemplate(defaultTemplate, templates);

    let isSave = false;
    let isTemplateSave = false;
    if (formData.changelogFile !== changelogFile) {
      isSave = true;
      handleChangeLogFile(formData.changelogFile);
    }

    if (showPRTemplate && (prTemplate !== formData.prTemplate || isPrCreationEnabled !== formData.prTemplateEnabled)) {
      handlePRTemplateChange(formData.prTemplateEnabled, formData.prTemplate);
      isSave = true;
    }

    if (buildSystemTemplatesEnabled !== formData.buildSystemTemplatesEnabled) {
      isSave = true;
      handleTemplateSwitch(formData.buildSystemTemplatesEnabled);
    }

    const hasTemplateChanges = templateValue !== formData.template;
    const hasArtefactChanges = isArtefactFormEnabled && artefactID !== formData.artefactID;
    const hasParentArtefactChanges = isArtefactFormEnabled && parentArtefactID !== formData.parentArtefactID;
    let hasCodegenWarning = hasTemplateChanges || hasArtefactChanges || hasParentArtefactChanges;
    const handleCodegenWarning = async () => {
      if (hasTemplateChanges) {
        await handleSubmitTemplate();
        isTemplateSave = true;
      }
      if (hasArtefactChanges) {
        await artefactIDMutation.mutateAsync(
          { uid, artefactID: formData.artefactID, entity: Entity.Project },
          {
            onSuccess: () => {
              isSave = true;
              queryClient.invalidateQueries(GitQueryKeys.Info);
              queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
            }
          }
        );
      }
      if (hasParentArtefactChanges) {
        await parentArtefactIDForProjectMutation.mutateAsync(
          { uid, artefactID: formData.parentArtefactID },
          {
            onSuccess: () => {
              isSave = true;
              queryClient.invalidateQueries(GitQueryKeys.Info);
              queryClient.invalidateQueries([QueryKeys.Git, QueryKeys.Project]);
            }
          }
        );
      }
    };

    if (hasCodegenWarning) {
      await handleRebuildCode({
        handleCallback: handleCodegenWarning,
        title: 'Update Build System',
        codeGenWarning: 'Updating the settings will update code for all pipelines'
      });
    }

    queryClient.invalidateQueries([
      QueryKeys.Project,
      QueryKeys.ProjectDeploymentSettings,
      QueryKeys.ProjectGeneralSettings
    ]);
    if (isTemplateSave || isSave) {
      toast.success({
        content:
          hasTemplateChanges && !isTemplateSave
            ? 'Settings changes saved except Build system.'
            : 'Settings saved successfully'
      });
    }
  }
  function isFormDirty() {
    if (data) {
      const {
        defaultTemplate,
        templates,
        buildSystemTemplatesEnabled: _buildSystemTemplatesEnabled,
        changelogFile: _changelogFile,
        artefactID: _artefactID,
        parentArtefactID: _parentArtefactID
      } = data;
      const { templateId: _templateId, templateValue: _templateValue } = getTemplate(defaultTemplate, templates);
      const _prTemplate = pullRequestData?.prTemplate || '';
      const _prTemplateEnabled = pullRequestData?.isPrCreationEnabled || false;

      return !(
        changelogFile === _changelogFile &&
        artefactID === _artefactID &&
        parentArtefactID === _parentArtefactID &&
        templateId === _templateId &&
        template === _templateValue &&
        buildSystemTemplatesEnabled === _buildSystemTemplatesEnabled &&
        prTemplate === _prTemplate &&
        prTemplateEnabled === _prTemplateEnabled
      );
    }

    return false;
  }
  const _isFormDirty = isFormDirty();
  const buttonDisabled =
    !(_isFormDirty && isValid) ||
    isUpdating ||
    prChangeInProgress ||
    isBuildSystemMutationLoading ||
    artefactIDMutation.isLoading ||
    parentArtefactIDForProjectMutation.isLoading;
  const loading =
    isUpdating ||
    prChangeInProgress ||
    isBuildSystemMutationLoading ||
    artefactIDMutation.isLoading ||
    parentArtefactIDForProjectMutation.isLoading;
  return (
    <Stack gap={theme.spaces.x16} align='space-between' height='100%'>
      <Stack
        padding={`0 ${theme.spaces.x10} ${theme.spaces.x10} ${theme.spaces.x10}`}
        grow='1'
        width='100%'
        gap={theme.spaces.x32}>
        {isSqlProject ? null : (
          <Stack height='400px' gap={theme.spaces.x24}>
            <Stack direction='horizontal' align='space-between'>
              <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                Build System
              </Text>
              <Stack direction='horizontal' gap={theme.spaces.x24}>
                {templateId ? (
                  <Button
                    size='s'
                    variant='secondaryGray'
                    loading={restoreDefaultTemplateMutation.isLoading}
                    onClick={() => {
                      restoreDefaultTemplateMutation.mutateAsync();
                    }}
                    data-test-id='project-settings-restore-default-button'>
                    Restore default
                  </Button>
                ) : null}
                <Tooltip
                  overlayStyle={{ width: '250px' }}
                  title='Contact Prophecy Support to inquire about other build systems support (e.g. SBT or Gradle)'>
                  <Stack width='150px'>
                    <Select
                      size='s'
                      options={BuildOptions}
                      value={entityName}
                      disabled={true}
                      data-test-id='project-settings-build-system-select'
                      aria-label='Build system'
                    />
                  </Stack>
                </Tooltip>
                <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
                  <Controller
                    render={({ field }) => (
                      <Switch
                        disabled={isReadMode || loading}
                        checked={field.value}
                        onChange={field.onChange}
                        data-test-id='settings-build-system-templates-switch'>
                        Enabled
                      </Switch>
                    )}
                    control={control}
                    name='buildSystemTemplatesEnabled'
                  />
                  <Tooltip
                    overlayStyle={{ width: '300px' }}
                    title={
                      buildSystemTemplatesEnabled
                        ? 'Generate build files using template'
                        : 'Build-System-Templating is disabled for your project. We recommend you to enable it.'
                    }>
                    <Button
                      icon={<InfoCircleIcon type='default' />}
                      variant='plain'
                      data-test-id='project-settings-info-button'
                      aria-label='Build system templates information'
                    />
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>

            <Stack grow='1' gap={theme.spaces.x12}>
              {showDiff && (
                <CheckForTemplateUpgrade
                  onClose={() => toggleDiff(false)}
                  defaultTemplate={data.defaultTemplate || ''}
                  templates={data.templates || FROZEN_EMPTY_ARRAY}
                  showDiff={showDiff}
                />
              )}
              {templateId ? (
                <Alert variant='info'>
                  <Stack direction='horizontal' width='100%' alignY='center' align='space-between'>
                    The project is using the custom template as a guide for building the project.
                    <Button
                      size='s'
                      variant='secondaryGray'
                      onClick={() => toggleDiff(true)}
                      data-test-id='project-settings-show-diff-button'>
                      Show Diff
                    </Button>
                  </Stack>
                </Alert>
              ) : null}

              <Controller
                rules={{
                  required: 'Build template is required',
                  validate: (value) => {
                    const errorMessageOrBoolean = isValidYML(value);
                    if (errorMessageOrBoolean !== true) {
                      return errorMessageOrBoolean;
                    }
                  }
                }}
                render={({ field }) => {
                  return (
                    <>
                      <Stack grow='1' padding={`0 ${theme.spaces.x16}`}>
                        <Editor
                          height='100%'
                          ariaLabel={`Editor for template`}
                          language='yaml'
                          monacoEditorProps={{ options: { detectIndentation: false, tabSize: 2 } }}
                          readOnly={isReadMode || loading || !buildSystemTemplatesEnabled}
                          path='language'
                          value={field.value}
                          onChange={field.onChange}
                        />
                      </Stack>
                      {errors.template?.message && (
                        <StyledHelpText variant={'error'} direction='horizontal'>
                          {renderColumnWithEllipsis(errors.template?.message)}
                        </StyledHelpText>
                      )}
                    </>
                  );
                }}
                control={control}
                name='template'
              />
            </Stack>
          </Stack>
        )}

        {showPRTemplate && (
          <Stack gap={theme.spaces.x24}>
            <Stack direction='horizontal' align='space-between'>
              <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
                Pull Request Template
              </Text>
              <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
                <Controller
                  render={({ field }) => (
                    <Switch
                      disabled={loading || isReadMode}
                      checked={field.value}
                      onChange={field.onChange}
                      data-test-id='settings-pr-template-switch'>
                      Enabled
                    </Switch>
                  )}
                  control={control}
                  name='prTemplateEnabled'
                />

                <Tooltip
                  overlayStyle={{ width: '300px' }}
                  title='Enable custom pull request template for your repository'>
                  <Button
                    icon={<InfoCircleIcon type='default' />}
                    variant='plain'
                    data-test-id='project-settings-info-button'
                    aria-label='Enable custom pull request template for your repository'
                  />
                </Tooltip>
              </Stack>
            </Stack>
            <Controller
              rules={{
                required: 'PR template is required'
              }}
              render={({ field }) => (
                <Input
                  disabled={isReadMode || loading}
                  label='Pull Request Template URL'
                  data-test-id='project-settings-pr-template-input'
                  {...field}
                  variant={errors.prTemplate ? 'error' : 'primary'}
                  helpText={errors.prTemplate?.message}
                />
              )}
              control={control}
              name='prTemplate'
            />
          </Stack>
        )}
        <Stack gap={theme.spaces.x24}>
          <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
            Change Log File
          </Text>
          <Controller
            rules={{
              required: 'File Path is required'
            }}
            render={({ field }) => (
              <Input
                label='Change Log File Path'
                data-test-id='project-settings-change-log-file-input'
                {...field}
                disabled={isReadMode || loading}
                variant={errors.changelogFile ? 'error' : 'primary'}
                helpText={errors.changelogFile?.message}
              />
            )}
            control={control}
            name='changelogFile'
          />
        </Stack>

        {isArtefactFormEnabled && (
          <Stack gap={theme.spaces.x24}>
            <Text level='lg' weight={theme.fontWeight.medium} tone={theme.colors.gray900}>
              Artifact
            </Text>
            <Stack gap={theme.spaces.x12}>
              <Stack direction='horizontal' gap={theme.spaces.x12}>
                <StackItem grow='1'>
                  <Controller
                    rules={{
                      required: ArtefactRequireMessage,
                      validate: validateEntityName
                    }}
                    render={({ field }) => (
                      <Input
                        label={ArtefactIdLabel}
                        data-test-id='project-settings-artefact-id-input'
                        {...field}
                        disabled={isReadMode || loading}
                        variant={errors.artefactID ? 'error' : 'primary'}
                        helpText={errors.artefactID?.message}
                      />
                    )}
                    control={control}
                    name='artefactID'
                  />
                </StackItem>
                <StackItem grow='1'>
                  <Controller
                    rules={{
                      required: ParentArtefactRequiredMessage,
                      validate: validateEntityName
                    }}
                    render={({ field }) => (
                      <Input
                        label={ParentArtefactIdLabel}
                        data-test-id='project-settings-parent-artefact-id-input'
                        {...field}
                        disabled={isReadMode || loading}
                        variant={errors.parentArtefactID ? 'error' : 'primary'}
                        helpText={errors.parentArtefactID?.message}
                      />
                    )}
                    control={control}
                    name='parentArtefactID'
                  />
                </StackItem>
              </Stack>
              <Alert variant='info'>
                {ArtefactNoteLine1}
                <br />
                {ArtefactNoteLine2}
                <br />
                {ArtefactNoteLine3}
                <br />
                {ArtefactNoteLine4}
              </Alert>
            </Stack>
          </Stack>
        )}
      </Stack>
      <SaveButton
        loading={loading || isLoading}
        disabled={buttonDisabled}
        onClick={handleSubmit(submit)}
        data-test-id='project-settings-save-button'>
        Save Changes
      </SaveButton>
    </Stack>
  );
}

enum DeploymentMode {
  Simultaneous = 'Deploy on Release',
  Staged = 'Staged Release and Deployment',
  Selective = 'Selective Job Deployment'
}

const DeploymentModeOptions = [
  {
    label: DeploymentMode.Simultaneous,
    value: DeploymentMode.Simultaneous,
    description: (
      <StyledText>
        When selected, <strong>Release and Deploy</strong> step automates pipeline building and job deployment without
        manual intervention.
      </StyledText>
    ),
    width: '33%',
    dataTestId: 'deployment-mode-simultaneous-radio-group-item'
  },
  {
    label: DeploymentMode.Staged,
    value: DeploymentMode.Staged,
    description: (
      <>
        <StyledText>
          When selected, <strong>Release</strong> and <strong>Deploy</strong> are separated into two steps.
        </StyledText>
        <StyledText>
          <strong>Deploy</strong> step handles pipeline building and job deployment, and users can choose where they
          want to deploy.
        </StyledText>
      </>
    ),
    width: '33%',
    dataTestId: 'deployment-mode-staged-radio-group-item'
  },
  {
    label: DeploymentMode.Selective,
    value: DeploymentMode.Selective,
    description: (
      <>
        <StyledText>
          When selected, <strong>Deploy</strong> step allows users to choose jobs for selective deployment.{' '}
        </StyledText>
        <StyledText>
          Kindly note that only the deployed jobs will have the latest pipelines, datasets and subgraphs.
        </StyledText>
      </>
    ),
    width: '33%',
    dataTestId: 'deployment-mode-selective-radio-group-item'
  }
];

const DeploymentSettingsTab = ({
  uid,
  data,
  isProjectAdmin,
  isSqlProject
}: SettingsProps & { isSqlProject?: boolean }) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useGraphQlMutation(updateProject);
  const { track } = useMixPanel();

  const deploymentSettings = data.deploymentSettings;

  const formContext = useForm<NonNullable<typeof deploymentSettings>>({
    mode: 'onChange',
    defaultValues: deploymentSettings || {
      isAutoDeployEnabled: true,
      isSelectiveJobDeploymentEnabled: false,
      isUTEnabled: false
    }
  });

  const { data: gitInfoData } = useGitInfo(uid);
  const backendCodingLanguage = gitInfoData?.Project?.backendLanguage;
  const hideStagedReleaseAndDeployment = isSqlProject && backendCodingLanguage !== BackEndCodeLanguage.python;

  const {
    control,
    handleSubmit,
    formState: { isDirty }
  } = formContext;

  const autoDeploy = formContext.watch('isAutoDeployEnabled');
  const isSelectiveJobDeploymentEnabled = formContext.watch('isSelectiveJobDeploymentEnabled');
  const onSubmit = usePersistentCallback(async (formData: NonNullable<typeof deploymentSettings>) => {
    track(
      ReleaseRevampAnalytics.events['deployment.settingChanged'],
      { deployment_mode: mode, projectId: uid },
      ReleaseRevampAnalytics.event_category
    );
    await mutateAsync({ uid, projectDeploymentSettings: JSON.stringify(formData) });
    queryClient.invalidateQueries([QueryKeys.Project, QueryKeys.ProjectDeploymentSettings]);
    formContext.reset(formData);
    toast.success({ content: 'Deployment settings saved successfully' });
  });

  const [mode, setMode] = useState(() =>
    autoDeploy
      ? hideStagedReleaseAndDeployment
        ? DeploymentMode.Staged
        : DeploymentMode.Simultaneous
      : isSelectiveJobDeploymentEnabled
        ? DeploymentMode.Selective
        : DeploymentMode.Staged
  );

  const onModeChange = usePersistentCallback((value) => {
    setMode(value);
    switch (value) {
      case DeploymentMode.Simultaneous: {
        formContext.setValue('isAutoDeployEnabled', true, DIRTY_SET_VALUE_CONFIG);
        formContext.setValue('isSelectiveJobDeploymentEnabled', false, DIRTY_SET_VALUE_CONFIG);
        break;
      }
      case DeploymentMode.Staged: {
        formContext.setValue('isAutoDeployEnabled', false, DIRTY_SET_VALUE_CONFIG);
        formContext.setValue('isSelectiveJobDeploymentEnabled', false, DIRTY_SET_VALUE_CONFIG);
        break;
      }
      case DeploymentMode.Selective: {
        formContext.setValue('isAutoDeployEnabled', false, DIRTY_SET_VALUE_CONFIG);
        formContext.setValue('isSelectiveJobDeploymentEnabled', true, DIRTY_SET_VALUE_CONFIG);
        break;
      }
    }
  });

  const deploymentModeOptions = useMemo(() => {
    if (hideStagedReleaseAndDeployment) {
      return DeploymentModeOptions.filter((op) => op.value !== DeploymentMode.Simultaneous);
    }
    return DeploymentModeOptions;
  }, [hideStagedReleaseAndDeployment]);

  return (
    <Stack
      gap={theme.spaces.x48}
      height='100%'
      padding={`0 ${theme.spaces.x10} ${theme.spaces.x10} ${theme.spaces.x10}`}>
      <Stack grow='1' gap={theme.spaces.x32}>
        <StyledRadioGroup
          value={mode}
          wrap={false}
          onChange={onModeChange}
          gap={theme.spaces.x12}
          options={deploymentModeOptions}
          orientation='horizontal'
        />
        <Controller
          render={({ field }) => (
            <Switch {...field} checked={field.value} defaultChecked data-test-id='settings-unit-tests-switch'>
              Enable Unit Tests
            </Switch>
          )}
          helpText={
            <>
              Enable this to Run Tests as part of building wheel/jar file of the Pipelines. Please note, this might lead
              to an increased build time.
            </>
          }
          control={control}
          name='isUTEnabled'
        />
      </Stack>
      <div>
        <Button
          loading={isLoading}
          disabled={!isDirty || isLoading || !isProjectAdmin}
          tooltip={!isProjectAdmin ? 'Only project admin can save' : undefined}
          onClick={handleSubmit(onSubmit)}
          data-test-id='deployment-settings-save-button'>
          Save Changes
        </Button>
      </div>
    </Stack>
  );
};

export const Settings = ({ uid, queriedBranch, isSimpleVersionControl, ...rest }: AdvancedProps) => {
  const globalConf = useGlobalVariables();
  const isReleaseV2Enabled = globalConf.isReleaseV2Enabled;
  const isProjectTemplateEnabled = globalConf.isProjectTemplateEnabled;

  const { data, isLoading } = useGraphQlQueryFn(
    SettingTabKey,
    fetchProjectSettings(false),
    { queryName: `Settings-fetchProjectSettings` },
    {
      projectId: uid,
      branch: queriedBranch
    }
  );

  if (!data || isLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  const showGeneralTab = isProjectTemplateEnabled;
  const showDeploymentTab = isReleaseV2Enabled && !isSimpleVersionControl;

  return (
    <>
      {showGeneralTab && showDeploymentTab ? (
        <InlineSwitchTabs size='s'>
          <Tabs.TabPane key='General' tab='General'>
            <GeneralSettingsTab {...rest} {...{ uid, data }} />
          </Tabs.TabPane>

          <Tabs.TabPane key='Deployment' tab='Deployment'>
            <DeploymentSettingsTab {...rest} {...{ uid, data }} />
          </Tabs.TabPane>
        </InlineSwitchTabs>
      ) : showGeneralTab ? (
        <GeneralSettingsTab {...rest} {...{ uid, data }} />
      ) : showDeploymentTab ? (
        <DeploymentSettingsTab {...rest} {...{ uid, data }} />
      ) : null}
    </>
  );
};
